import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { FileText, Clock, CheckCircle, XCircle, Send, Upload, File, X, Download, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VerificationBanner({ user }) {
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified !== false || user.auth_method !== 'email') return null;

  const resend = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      toast.success('Verification email sent! Check your console/inbox.');
    } catch {
      toast.error('Failed to resend verification email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="verification-banner">
      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-accent text-sm font-medium text-amber-800">Please verify your email</p>
        <p className="font-body text-xs text-amber-700 mt-1">
          Check your inbox for a verification link. This helps secure your account.
        </p>
      </div>
      <button
        onClick={resend}
        disabled={sending}
        className="font-accent text-xs text-amber-700 hover:text-amber-900 underline shrink-0"
        data-testid="resend-verification-btn"
      >
        {sending ? 'Sending...' : 'Resend'}
      </button>
    </div>
  );
}

export default function StudentDashboard() {
  const { user, updateRole } = useAuth();
  const [applications, setApplications] = useState([]);
  const [essayTitle, setEssayTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchApplications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scholarships/my-applications`, { withCredentials: true });
      setApplications(res.data);
    } catch (err) {
      console.error('Failed to fetch applications:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) {
      toast.error('Only PDF and DOCX files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/files/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedFile(res.data);
      toast.success('Essay file uploaded successfully');
    } catch (err) {
      const msg = err.response?.data?.detail || 'File upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!essayTitle.trim()) {
      toast.error('Please enter your essay title');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/scholarships/apply`,
        { essay_title: essayTitle, file_id: uploadedFile?.file_id || null },
        { withCredentials: true }
      );
      toast.success('Application submitted successfully!');
      setEssayTitle('');
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchApplications();
    } catch {
      toast.error('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'essay';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  const switchToDonor = async () => {
    await updateRole('donor');
    toast.success('Switched to Donor view');
  };

  const statusConfig = {
    submitted: { icon: Clock, label: 'Under Review', color: 'bg-brass/20 text-ink' },
    reviewed: { icon: FileText, label: 'Reviewed', color: 'bg-blue-100 text-blue-800' },
    awarded: { icon: CheckCircle, label: 'Awarded', color: 'bg-brass text-ink' },
    rejected: { icon: XCircle, label: 'Not Selected', color: 'bg-red-100 text-red-800' },
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="student-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight">
              Student Dashboard
            </h1>
            <p className="font-body text-sm text-ink/60 mt-1">
              Welcome back, {user?.name || 'Student'}
            </p>
          </div>
          <Button
            onClick={switchToDonor}
            variant="outline"
            className="border-2 border-cabernet text-cabernet hover:bg-cabernet/5 font-accent text-sm"
            data-testid="switch-to-donor-btn"
          >
            Switch to Donor Portal
          </Button>
        </div>

        <VerificationBanner user={user} />

        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-card-surface border border-brass/20">
            <TabsTrigger value="applications" className="font-accent text-sm data-[state=active]:bg-cabernet data-[state=active]:text-cream" data-testid="tab-applications">
              My Applications
            </TabsTrigger>
            <TabsTrigger value="apply" className="font-accent text-sm data-[state=active]:bg-cabernet data-[state=active]:text-cream" data-testid="tab-apply">
              New Application
            </TabsTrigger>
          </TabsList>

          <TabsContent value="applications">
            <div className="space-y-4">
              {applications.length === 0 ? (
                <div className="bg-card-surface rounded-lg border border-brass/20 p-12 text-center">
                  <FileText className="w-10 h-10 text-brass/50 mx-auto mb-3" />
                  <p className="font-body text-ink/60">No applications yet. Submit your first application!</p>
                </div>
              ) : (
                applications.map((app) => {
                  const config = statusConfig[app.status] || statusConfig.submitted;
                  const StatusIcon = config.icon;
                  return (
                    <div
                      key={app.application_id}
                      className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-6"
                      data-testid={`application-card-${app.application_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-heading text-lg font-medium text-cabernet">
                            {app.scholarship_name}
                          </h3>
                          <p className="font-body text-sm text-ink/70 mt-1">
                            Essay: "{app.essay_title}"
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <p className="font-accent text-xs text-ink/50">
                              Submitted: {new Date(app.submitted_at).toLocaleDateString()}
                            </p>
                            {app.file_id && (
                              <button
                                onClick={() => handleDownload(app.file_id, app.essay_title)}
                                className="flex items-center gap-1 font-accent text-xs text-cabernet hover:text-cabernet-hover transition-colors"
                                data-testid={`download-essay-${app.application_id}`}
                              >
                                <Download className="w-3 h-3" />
                                Download Essay
                              </button>
                            )}
                          </div>
                        </div>
                        <Badge className={`${config.color} font-accent text-xs flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {config.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="apply">
            <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-8">
              <h2 className="text-2xl font-heading font-medium text-cabernet mb-2">
                The Global Visionaries Scholarship
              </h2>
              <p className="font-body text-sm text-ink/60 mb-6">
                Real Estate & Entrepreneurship Essay Application
              </p>

              <div className="bg-cream rounded-md p-5 border-l-4 border-brass mb-6">
                <p className="font-accent text-xs font-semibold text-ink/80 uppercase tracking-wider mb-2">Essay Prompt</p>
                <p className="font-body text-sm text-ink/75 italic leading-relaxed">
                  "Describe your long-term career aspirations within the realms of real estate, 
                  business, or international entrepreneurship. How will firsthand exposure to 
                  global markets and international development projects accelerate your professional 
                  growth, and why are you the ideal candidate to represent this organization abroad?"
                </p>
              </div>

              <div className="space-y-3 mb-6 font-accent text-sm text-ink/70">
                <p><strong>Format:</strong> APA Style (7th Edition)</p>
                <p><strong>Length:</strong> Maximum 1 page (excluding Title Page and References)</p>
              </div>

              <form onSubmit={handleApply} className="space-y-4">
                <div>
                  <label className="font-accent text-sm font-medium text-ink mb-1 block">
                    Essay Title
                  </label>
                  <Input
                    value={essayTitle}
                    onChange={(e) => setEssayTitle(e.target.value)}
                    placeholder="Enter your essay title..."
                    className="bg-cream border-cabernet/20 focus:ring-brass font-body"
                    data-testid="essay-title-input"
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="font-accent text-sm font-medium text-ink mb-1 block">
                    Upload Essay (PDF or DOCX)
                  </label>
                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.doc"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="essay-file-input"
                      data-testid="essay-file-input"
                    />
                    {!uploadedFile ? (
                      <label
                        htmlFor="essay-file-input"
                        className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-cabernet/20 rounded-lg cursor-pointer hover:border-brass hover:bg-brass/5 transition-colors"
                        data-testid="file-upload-dropzone"
                      >
                        {uploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-cabernet border-t-transparent rounded-full animate-spin"></div>
                            <span className="font-accent text-sm text-ink/60">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-brass" />
                            <span className="font-accent text-sm text-ink/60">
                              Click to upload PDF or DOCX (max 10MB)
                            </span>
                          </>
                        )}
                      </label>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-cream border border-brass/30 rounded-lg" data-testid="uploaded-file-info">
                        <File className="w-5 h-5 text-brass shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-accent text-sm text-ink truncate">{uploadedFile.filename}</p>
                          <p className="font-accent text-xs text-ink/50">
                            {(uploadedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-ink/40 hover:text-cabernet transition-colors"
                          data-testid="remove-file-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm px-6 py-5"
                  data-testid="submit-application-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
